<template>
  <v-wait for="isPreparingReport">
    <core-spinner slot="waiting" />
    <title-bar :show-back="true">
      <ProjectSelector
        disable-project
        class="col-5"
      />
    </title-bar>
    <v-container fluid>
      <material-card outlined class="max-width-content mx-auto card-inputs-without-padding pa-4">
        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              :value="report && $t(`enums.reportTypes.${report.type}`)"
              :label="$t('views.reports.report-type')"
              readonly
            />
            <v-text-field
              :value="
                report && $t(`enums.reportSources.${report.source}`)
              "
              :label="$t('views.reports.source')"
              readonly
            />
            <v-text-field
              :value="report && report.impact"
              :label="$t('views.reports.impact')"
              readonly
            />
            <v-text-field
              v-if="report && report.nature"
              :label="$t('views.reports.nature')"
              :value="
                report && $t(`enums.reportNatures.${report.nature}`)
              "
              readonly
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              :label="$t('views.reports.author')"
              :value="report && report.author"
              readonly
            />
            <v-text-field
              :label="$t('views.reports.created-at')"
              :value="
                report &&
                $dayjs(report.createdAt).format('YYYY-MM-DD H:mm')
              "
              readonly
            />
            <v-text-field
              :label="$t('views.reports.updated-at')"
              :value="
                report &&
                $dayjs(report.updatedAt).format('YYYY-MM-DD H:mm')
              "
              readonly
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="image in images"
            :key="image._id"
            cols="12"
            sm="6"
            md="4"
            xl="2"
          >
            <v-card class="thumbnail-card">
              <v-img
                :src="image.src"
                class="white--text align-end"
                height="250"
                width="250"
              >
                <v-card-title>
                  <div class="text-truncate">
                    {{ image.originalFilename }}
                  </div>
                </v-card-title>
              </v-img>
              <v-card-actions>
                <v-spacer />
                <v-btn
                  icon
                  @click="$refs.imageDialog.show(image)"
                >
                  <v-icon>$fas fa-expand-alt</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
        <ResizableImageDialog ref="imageDialog" />
      </material-card>
      <material-card outlined class="max-width-content mx-auto card-inputs-without-padding pa-4 mt-8">
        <v-form ref="reportForm">
          <v-select
            v-if="isReportInProgress"
            v-model="selectedState"
            :items="reportStates"
            :label="$t('views.reports.report-state')"
            class="select-no-border"
            style="max-width: 320px;"
            :menu-props="{
              nudgeBottom: 1000,
            }"
          />
          <material-card
            v-for="(version, index) of descriptionVersions"
            :key="index"
            outlined
            class="my-2"
          >
            <SectionTitle 
              :title="version.user.name" 
              :title-detail="$dayjs(version.timestamp).format('YYYY-MM-DD H:mm')" />
            <span class="text-body-1 mt-2"> {{ version.text }}</span>
          </material-card>

          <v-btn
            v-if="!isReportInProgress"
            :loading="isLoading"
            color="primary"
            class="mt-4"
            @click="setInProgress"
          >
            {{ $t('views.reports.markInProgress') }}
          </v-btn>

          <div v-if="isReportInProgress">
            <v-textarea
              v-model="description"
              :label="$t('views.reports.description')"
              filled
            />
            <v-btn
              :loading="isLoading"
              color="primary"
              @click="update"
            >
              {{ $t('views.reports.confirm') }}
            </v-btn>
          </div>
        </v-form>
      </material-card>
    </v-container>
  </v-wait>
</template>

<script>
import TitleBar from '@/components/core/TitleBar'
import { mapActions, mapGetters } from 'vuex'
import { reportStates } from '@/utils/enum'
import { toSelectItems } from '@/utils/i18n'
import axios from 'axios'
import ResizableImageDialog from '@/components/DashViews/Image/ResizableImageDialog'
import ProjectSelector from '../../core/ProjectSelector.vue'
import SectionTitle from '@/components/core/SectionTitle.vue'

export default {
  name: 'EditReport',
  components: {
    TitleBar,
    ResizableImageDialog,
    ProjectSelector,
    SectionTitle
  },
  props: {
    reportId: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    selectedState: null,
    description: '',
    isLoading: false,
    images: [],
    historyDialog: false,
  }),

  computed: {
    ...mapGetters({ getReportById: 'reports/getById' }),

    report() {
      return this.getReportById(this.reportId)
    },
    reportStates() {
      return toSelectItems(reportStates, 'enums.reportStates')
    },
    descriptionVersions() {
      return (this.report && this.report.description) || []
    },
    isReportInProgress() {
      return this.selectedState !== 'created'
    },
  },

  async created() {
    this.$wait.start('isPreparingReport')
    await this.fetchReport(this.reportId)
    this.selectedState = this.report && this.report.state
    await this.prepareImages()
    this.$wait.end('isPreparingReport')
  },

  methods: {
    ...mapActions({
      fetchReport: 'reports/fetchById',
      updateReport: 'reports/update',
    }),

    async update() {
      if (!this.$refs.reportForm.validate()) return

      this.isLoading = true
      try {
        await this.updateReport({
          _id: this.report._id,
          state: this.selectedState,
          description: this.description,
        })
        this.description = ''
      } finally {
        this.isLoading = false
      }
    },

    async setInProgress() {
      this.isLoading = true

      try {
        await this.updateReport({
          _id: this.report._id,
          state: 'processing',
        })
        this.selectedState = 'processing'
      } finally {
        this.isLoading = false
      }
    },

    async prepareImages() {
      const images = [...this.report.pictures.map((_p) => _p.image)]
      if (this.report.memoPicture) {
        images.push(this.report.memoPicture)
      }

      await Promise.all(
        images.map(async (_image) => {
          if (_image.src) {
            return
          }
          try {
            const { data } = await axios.get(
              `/images/thumbnail/${_image._id}`,
              { responseType: 'blob' },
            )
            _image.src = URL.createObjectURL(data)
          } catch (err) {
            console.warn(err)
          }
        }),
      )

      this.images = images
    },
  },
}
</script>
